import { clamp, getImageCrop } from "./ShareCardLayout";
import { constrainImageElement, DEFAULT_IMAGE_SHADOW } from "./ShareCardElements";
import { getPinView, renderPinMaterial } from "./SharePinMaterial";
import { renderTapeContact } from "./ShareTapeContact";

export const DECORATIONS = [
  { id: "gold-pin", label: "Gold pin", type: "pin", aspect: 1 },
  { id: "silver-pin", label: "Silver pin", type: "pin", aspect: 1 },
  { id: "red-pin", label: "Red pin", type: "pin", aspect: 1 },
  { id: "paper-tape", label: "Paper tape", type: "tape", aspect: 3.4 },
  { id: "clear-tape", label: "Clear tape", type: "tape", aspect: 3.4 },
];


export const createDecorationElement = (scene, decorationId, topZ, target, metrics, trimPadding = true) => {
  const preset = DECORATIONS.find((item) => item.id === decorationId) || DECORATIONS[0];
  const cardWidth = target?.kind === "card" ? scene.baseCardWidth * target.scale : scene.baseCardWidth;
  const width = cardWidth * (preset.type === "pin" ? 0.3 : 0.55);
  let x = scene.width / 2;
  let y = scene.height / 2;
  let rotation = preset.type === "tape" ? -9 : 0;
  if (target?.kind === "card") {
    const crop = getImageCrop(metrics, trimPadding);
    const height = cardWidth * crop.height / crop.width;
    const angle = target.rotation * Math.PI / 180;
    // The pin's base (and the tape's center) is the placement anchor.
    x = target.x + Math.sin(angle) * height * 0.47;
    y = target.y - Math.cos(angle) * height * 0.47;
    if (preset.type === "tape") rotation += target.rotation;
  } else if (target?.decoration) {
    x = target.x + width * 0.5;
    y = target.y + width * 0.25;
  }
  return constrainImageElement({
    id: `decoration-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    kind: "image", decoration: preset.id, label: preset.label,
    x, y, width, height: width / preset.aspect, baseWidth: width, rotation, z: topZ + 1,
    depth: 1, opacity: 1, textureSeed: Math.floor(Math.random() * 100000),
    shadow: { ...DEFAULT_IMAGE_SHADOW },
  }, scene);
};

const rotateVector = (x, y, angle) => ({
  x: x * Math.cos(angle) - y * Math.sin(angle),
  y: x * Math.sin(angle) + y * Math.cos(angle),
});

export const getPinProjection = (element, scene) => {
  const angle = -element.rotation * Math.PI / 180;
  const depth = clamp(element.depth ?? 1, 0.3, 1.3);
  const view = getPinView(element, scene);
  const sideLength = Math.hypot(view[0], view[2]);
  const height = (0.22 + 0.065 * depth) * 115;
  return {
    stem: rotateVector(-view[0] / sideLength * height, -view[1] * view[2] / sideLength * height, angle),
    light: rotateVector(-0.62, -0.78, angle),
    shadow: rotateVector(8, 10, angle),
    baseSquash: view[2],
  };
};

const ellipse = (context, x, y, rx, ry, fill) => {
  context.beginPath();
  context.ellipse(x, y, rx, ry, 0, 0, Math.PI * 2);
  context.fillStyle = fill;
  context.fill();
};

const softShadow = (context, x, y, rx, ry, opacity) => {
  context.save();
  context.translate(x, y);
  context.scale(rx, ry);
  const gradient = context.createRadialGradient(0, 0, 0, 0, 0, 1);
  gradient.addColorStop(0, `rgba(0,0,0,${opacity})`);
  gradient.addColorStop(0.45, `rgba(0,0,0,${opacity * 0.65})`);
  gradient.addColorStop(1, "rgba(0,0,0,0)");
  ellipse(context, 0, 0, 1, 1, gradient);
  context.restore();
};

const drawPin = (context, element, scene) => {
  const { shadow, baseSquash } = getPinProjection(element, scene);
  softShadow(context, 50 + shadow.x, 50 + shadow.y, 20, 14 * baseSquash, 0.28);
  softShadow(context, 50, 52, 13, 8, 0.45);
  const transform = context.getTransform();
  const size = Math.max(144, Math.min(1024, Math.round(Math.hypot(transform.a, transform.b) * 100 * 1.5)));
  context.drawImage(renderPinMaterial(element, scene, size), 0, 0, 100, 100);
};

const seededRandom = (seed) => () => {
  seed = (Math.imul(1664525, seed) + 1013904223) >>> 0;
  return seed / 4294967296;
};

const tapePath = (context, seed) => {
  const random = seededRandom(seed);
  context.beginPath();
  context.moveTo(7, 15);
  for (let x = 11; x <= 91; x += 4) context.lineTo(x, 14 + random() * 3);
  for (let y = 18; y <= 82; y += 6) context.lineTo(91 + random() * 3, y);
  for (let x = 91; x >= 7; x -= 4) context.lineTo(x, 84 + random() * 3);
  for (let y = 81; y >= 15; y -= 6) context.lineTo(6 + random() * 3, y);
  context.closePath();
};

const drawTape = (context, element, surfaces) => {
  const clear = element.decoration === "clear-tape";
  const seed = element.textureSeed || 1;
  const light = rotateVector(-0.62, -0.78, -element.rotation * Math.PI / 180);
  // Tape is translucent. The surface beneath remains visible, including in the PNG.
  context.save();
  context.translate(0.3, 2);
  tapePath(context, seed);
  context.strokeStyle = clear ? "rgba(0,0,0,0.12)" : "rgba(0,0,0,0.2)";
  context.lineWidth = 2;
  context.stroke();
  context.restore();
  tapePath(context, seed);
  context.save();
  context.clip();
  const body = context.createLinearGradient(50 + light.x * 50, 50 + light.y * 50, 50 - light.x * 50, 50 - light.y * 50);
  body.addColorStop(0, clear ? "rgba(244,249,255,0.26)" : "rgba(251,231,186,0.8)");
  body.addColorStop(0.45, clear ? "rgba(219,230,239,0.12)" : "rgba(226,207,163,0.72)");
  body.addColorStop(1, clear ? "rgba(249,253,255,0.3)" : "rgba(253,235,197,0.84)");
  context.fillStyle = body;
  context.fillRect(0, 0, 100, 100);
  const random = seededRandom(seed);
  for (let index = 0; index < 620; index += 1) {
    const x = random() * 100;
    const y = random() * 100;
    const lightFiber = random() > 0.5;
    context.strokeStyle = lightFiber ? `rgba(255,255,240,${clear ? 0.06 : 0.17})` : `rgba(91,73,39,${clear ? 0.025 : 0.08})`;
    context.lineWidth = 0.2 + random() * 0.4;
    context.beginPath();
    context.moveTo(x, y);
    context.lineTo(x + 0.4 + random() * 2.5, y + random() * 0.8);
    context.stroke();
  }
  const contact = renderTapeContact(element, surfaces);
  if (contact) context.drawImage(contact, 0, 0, 100, 100);
  // Small creases and a lifted edge, with highlights following the shared light.
  for (const edge of [12, 89]) {
    const crease = context.createLinearGradient(edge - 2, 0, edge + 2, 0);
    crease.addColorStop(0, "rgba(0,0,0,0)");
    crease.addColorStop(0.45, "rgba(71,56,33,0.1)");
    crease.addColorStop(0.55, "rgba(255,255,255,0.27)");
    crease.addColorStop(1, "rgba(255,255,255,0)");
    context.fillStyle = crease;
    context.fillRect(edge - 2, 10, 4, 80);
  }
  const sheen = context.createLinearGradient(0, 12, 0, 29);
  sheen.addColorStop(0, "rgba(255,255,255,0.42)");
  sheen.addColorStop(1, "rgba(255,255,255,0)");
  context.fillStyle = sheen;
  context.fillRect(0, 12, 100, clear ? 24 : 6);
  context.restore();
  tapePath(context, seed);
  context.lineWidth = 0.6;
  context.strokeStyle = clear ? "rgba(255,255,255,0.32)" : "rgba(255,245,220,0.46)";
  context.stroke();
};

// Preview and export use this same renderer. Coordinates depend on normalized
// canvas placement, so increasing export resolution never changes perspective.
export const drawDecoration = (context, element, scene, surfaces = []) => {
  context.save();
  context.globalAlpha *= element.opacity ?? 1;
  context.scale(element.width / 100, element.height / 100);
  context.translate(-50, -50);
  if (element.decoration.endsWith("-pin")) drawPin(context, element, scene);
  else drawTape(context, element, surfaces);
  context.restore();
};

export const paintDecorationCanvas = (canvas, element, scene, pixelRatio = 1, minimumWidth = 1, maximumWidth = Infinity, surfaces = []) => {
  canvas.width = Math.max(minimumWidth, Math.min(maximumWidth, Math.round(element.width * pixelRatio)));
  canvas.height = Math.max(1, Math.round(canvas.width * element.height / element.width));
  const context = canvas.getContext("2d");
  if (!context) throw new Error("Canvas is not available in this browser.");
  context.scale(canvas.width / element.width, canvas.height / element.height);
  context.translate(element.width / 2, element.height / 2);
  drawDecoration(context, element, scene, surfaces);
  return canvas;
};
